// frontend/src/pages/AuditLogPage.js
import { useState, useEffect } from 'react';
import { Table, message, Typography, Tag, Input, List, Card, Space } from 'antd';
import { getAuditLogs } from '../services/logService';
import dayjs from 'dayjs';
import useResponsive from '../hooks/useResponsive';

const { Title, Text } = Typography;
const { Search } = Input;

const AuditLogPage = () => {
    const [logs, setLogs] = useState([]);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(20);
    const { isMobile } = useResponsive();

    useEffect(() => {
        const fetchLogs = async () => {
            setLoading(true);
            try {
                const data = await getAuditLogs();
                setLogs(data);
                setFilteredLogs(data); // Initially, show all logs
            } catch (error) {
                message.error('Failed to load audit logs.');
            } finally {
                setLoading(false);
            }
        };
        fetchLogs();
    }, []);

    const handleSearch = (value) => {
        const lowercasedValue = value.toLowerCase();
        const filtered = logs.filter(log =>
            log.username?.toLowerCase().includes(lowercasedValue) ||
            log.action_type?.toLowerCase().includes(lowercasedValue) ||
            log.module?.toLowerCase().includes(lowercasedValue) ||
            log.details?.toLowerCase().includes(lowercasedValue)
        );
        setFilteredLogs(filtered);
    };

    const columns = [
        {
            title: 'Timestamp',
            dataIndex: 'timestamp',
            key: 'timestamp',
            render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
            sorter: (a, b) => dayjs(a.timestamp).unix() - dayjs(b.timestamp).unix(),
            defaultSortOrder: 'descend',
        },
        { title: 'Username', dataIndex: 'username', key: 'username', sorter: (a, b) => a.username.localeCompare(b.username) },
        {
            title: 'Action Type',
            dataIndex: 'action_type',
            key: 'action_type',
            render: (type) => {
                let color = 'geekblue';
                if (type.includes('CREATE') || type.includes('LOGIN')) color = 'green';
                if (type.includes('UPDATE')) color = 'orange';
                if (type.includes('DELETE')) color = 'volcano';
                return <Tag color={color}>{type.toUpperCase()}</Tag>;
            }
        },
        { title: 'Module', dataIndex: 'module', key: 'module' },
        { title: 'Details', dataIndex: 'details', key: 'details', width: '40%' },
        { title: 'IP Address', dataIndex: 'ip_address', key: 'ip_address' },
    ];

    const renderMobileList = () => (
        <List
            dataSource={filteredLogs}
            loading={loading}
            pagination={{
                pageSize: pageSize,
                onChange: (page, size) => setPageSize(size)
            }}
            renderItem={(item) => {
                let color = 'geekblue';
                if (item.action_type.includes('CREATE') || item.action_type.includes('LOGIN')) color = 'green';
                if (item.action_type.includes('UPDATE')) color = 'orange';
                if (item.action_type.includes('DELETE')) color = 'volcano';

                return (
                    <List.Item>
                        <Card style={{ width: '100%' }} size="small">
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text strong>{item.username}</Text>
                                    <Tag color={color}>{item.action_type}</Tag>
                                </div>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    {dayjs(item.timestamp).format('YYYY-MM-DD HH:mm:ss')}
                                </Text>
                                <div>
                                    <Text strong>Module: </Text> <Text>{item.module}</Text>
                                </div>
                                <div>
                                    <Text strong>Details: </Text> <Text>{item.details}</Text>
                                </div>
                                <div style={{ fontSize: '12px', color: '#888' }}>
                                    IP: {item.ip_address}
                                </div>
                            </Space>
                        </Card>
                    </List.Item>
                );
            }}
        />
    );

    return (
        <div>
            <Title level={2} style={{ marginBottom: 16 }}>Audit Log</Title>
            <Search
                placeholder="Search logs (username, action, module, details...)"
                onSearch={handleSearch}
                style={{ marginBottom: 16 }}
                allowClear
            />
            {isMobile ? renderMobileList() : (
                <Table
                    dataSource={filteredLogs}
                    columns={columns}
                    loading={loading}
                    bordered
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        pageSize: pageSize,
                        showSizeChanger: true,
                        pageSizeOptions: [10, 20, 50, 100],
                        onShowSizeChange: (current, size) => setPageSize(size),
                        onChange: (page, size) => setPageSize(size),
                        responsive: true
                    }}
                />
            )}
        </div>
    );
};

export default AuditLogPage;